import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  Button,
  Chip,
  Collapse,
  Card,
  Pagination,
} from '@mui/material';
import {
  ArrowBack,
  OpenInNew,
  ContentCopy,
  History,
  ExpandMore,
  ExpandLess,
  RestorePage
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getMyHistory, getUserHistory } from '../services/api';
import ProductDisplay from '../components/shared/ProductDisplay';
import SubscriptionPlanDisplay from '../components/shared/SubscriptionPlanDisplay';
import HistoryFiltersPanel from '../components/shared/HistoryFiltersPanel';
import { 
  getActiveProductTypes, 
  getSnapshotFromOffer 
} from '../utils/snapshotUtils';
import { formatDisplayPrice, formatUSDInternalDisplayPrice } from '../utils/currencyUtils';
import { formatSnapshotPrice } from '../utils/pricingItemNormalizer';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { usePricing } from '../contexts/PricingContext';
import { useTabs } from '../contexts/TabsContext';
import { OfferComparisonTable } from './ComparisonPage';
import {
  buildCalculatorTemplateUrl,
  buildOfferTemplatePayload,
  buildComparisonTemplatePayloads,
  canUseOfferAsTemplate,
  getComparisonTemplateRegion,
  getOfferTemplateRegion,
} from '../utils/offerTemplateUtils';

const OffersHistoryPage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { displayCurrency, updatePreferences } = useUserPreferences();
  const { currencyRates } = usePricing();
  const { createTab } = useTabs();
  const { t } = useTranslation();

  const isAdminView = Boolean(userId);
  const [viewingUser, setViewingUser] = useState(null);
  const [loading, setLoading] = useState(true); // Initial loading
  const [tableLoading, setTableLoading] = useState(false); // Table refresh loading
  const [error, setError] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const [copiedItemId, setCopiedItemId] = useState(null);
  const [expandedComparison, setExpandedComparison] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [clientName, setClientName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [templateLoadingId, setTemplateLoadingId] = useState(null);
  const itemsPerPage = 50;
  const copyTimeoutRef = useRef(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  // Fetch history data with pagination and date filters
  const fetchHistory = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setTableLoading(true);
      }
      
      const response = isAdminView
        ? await getUserHistory(userId, currentPage, itemsPerPage, dateFrom, dateTo, debouncedClientName)
        : await getMyHistory(currentPage, itemsPerPage, dateFrom, dateTo, debouncedClientName);

      if (isAdminView && response.data.user) {
        setViewingUser(response.data.user);
      }
      
      // Transform history items to match our UI format
      const transformedItems = response.data.data.map(item => {
        if (item.type === 'comparison') {
          return {
            id: item.id,
            type: 'comparison',
            createdAt: new Date(item.created_at),
            userEmail: item.metadata?.generator_user_email || '',
            clientName: item.metadata?.client_name || '',
            totalOffers: item.metadata?.total_offers || item.offers?.length || 0,
            link: `/comparisons/${item.id}`,
            region: item.region,
            offers: item.offers || []
          };
        } else {
          return {
            id: item.id,
            type: 'offer',
            createdAt: new Date(item.created_at),
            userEmail: item.metadata?.generator_user_email || '',
            clientName: item.metadata?.client_name || '',
            link: `/offers/${item.id}`,
            offer: item,
            region: item.region,
            totalMonthly: item.pricing_snapshot?.total_monthly || item.total_price || 0
          };
        }
      });
      
      setHistoryItems(transformedItems);
      setTotalPages(response.data.pagination.total_pages);
      setTotalItems(response.data.pagination.total);
    } catch (err) {
      console.error('Error fetching history:', err);
      setError(err.response?.data?.error || 'Failed to load history');
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };

  // Debounce client name search
  const [debouncedClientName, setDebouncedClientName] = useState('');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedClientName(clientName);
    }, 300); // 300ms debounce
    
    return () => clearTimeout(timer);
  }, [clientName]);
  
  // Track if this is the very first load
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  
  // Fetch history when page or filters change
  useEffect(() => {
    const isInitialLoad = !hasInitiallyLoaded;
    if (isInitialLoad) {
      setHasInitiallyLoaded(true);
    }
    fetchHistory(isInitialLoad);
  }, [currentPage, dateFrom, dateTo, debouncedClientName]);

  
  const handleDateRangeChange = (from, to) => {
    setDateFrom(from);
    setDateTo(to);
    setCurrentPage(1); // Reset to first page when changing date range
  };
  
  const handleClearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setClientName('');
    setCurrentPage(1);
  };
  
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleCopyLink = async (link, itemId) => {
    try {
      const fullUrl = `${window.location.origin}${link}`;
      await navigator.clipboard.writeText(fullUrl);
      setCopiedItemId(itemId);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopiedItemId(null), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      setCopiedItemId(itemId);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopiedItemId(null), 2000);
    }
  };

  const handleOpenLink = (link) => {
    window.open(link, '_blank');
  };

  const createTemplateTabs = async (payloads, sourceId, templateRegion) => {
    if (!payloads.length) return;

    const calculatorWindow = window.open('', '_blank');
    setTemplateLoadingId(sourceId);
    try {
      const createdIds = [];
      for (const payload of payloads) {
        const newTabId = await createTab(payload);
        if (newTabId) createdIds.push(newTabId);
      }

      if (templateRegion) {
        await updatePreferences({ region: templateRegion });
      }

      if (createdIds.length > 0) {
        const templateUrl = buildCalculatorTemplateUrl(createdIds[0]);
        if (calculatorWindow) {
          calculatorWindow.opener = null;
          calculatorWindow.location.href = templateUrl;
        } else {
          window.open(templateUrl, '_blank', 'noopener,noreferrer');
        }
      } else if (calculatorWindow) {
        calculatorWindow.close();
      }
    } catch (err) {
      if (calculatorWindow) {
        calculatorWindow.close();
      }
      console.error('Failed to create template tabs from history:', err);
    } finally {
      setTemplateLoadingId(null);
    }
  };

  const handleUseAsTemplate = (item) => {
    const source = item.type === 'comparison'
      ? { id: item.id, region: item.region, offers: item.offers }
      : { ...item.offer, region: item.offer?.region || item.region };
    const payloads = item.type === 'comparison'
      ? buildComparisonTemplatePayloads(source)
      : [buildOfferTemplatePayload(item.offer)].filter(Boolean);
    const templateRegion = item.type === 'comparison'
      ? getComparisonTemplateRegion(source)
      : getOfferTemplateRegion(source);

    createTemplateTabs(payloads, item.id, templateRegion);
  };

  const formatDateTime = (date) => {
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleExpanded = (comparisonId) => {
    setExpandedComparison(expandedComparison === comparisonId ? null : comparisonId);
  };

  // Get monthly total from snapshot
  const getMonthlyTotal = (offer) => {
    const snapshot = getSnapshotFromOffer(offer);
    return snapshot?.total_monthly || offer?.total_price || 0;
  };

  const getValidOneTimePayments = (snapshot) => {
    return (snapshot?.one_time_payments || []).filter(payment => payment.total > 0);
  };

  const renderSectionTitle = (label, count = null) => (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      mb: 1.25,
    }}>
      <Typography sx={{
        fontSize: '0.72rem',
        fontWeight: 700,
        color: 'text.secondary',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}>
        {label}
      </Typography>
      {count !== null && (
        <Chip
          label={count}
          size="small"
          sx={{
            height: 20,
            borderRadius: 1,
            bgcolor: '#f3f4f6',
            color: 'text.secondary',
            fontSize: '0.68rem',
            fontWeight: 600,
          }}
        />
      )}
    </Box>
  );

  const renderOneTimePayments = (snapshot) => {
    const payments = getValidOneTimePayments(snapshot);
    if (!payments.length) return null;

    return (
      <Box>
        {renderSectionTitle(t('offer.includedOneTimeFees', 'Included One-time Fees'), payments.length)}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {payments.map((payment, index) => (
            <Box
              key={`${payment.description || 'payment'}-${index}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                gap: 2,
                alignItems: 'center',
                py: 0.75,
                borderBottom: index === payments.length - 1 ? 'none' : '1px solid #eef0f2',
              }}
            >
              <Typography sx={{ fontSize: '0.875rem', color: 'text.primary', minWidth: 0 }}>
                {payment.description || t('calculator.oneTimePayment', 'One-time payment')}
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: 'text.primary' }}>
                {formatUSDInternalDisplayPrice(payment.total, snapshot?.region, displayCurrency, currencyRates)}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
          {t('offer.addedToPlanBelow', '↓ Added to subscriptions below')}
        </Typography>
      </Box>
    );
  };

  const renderSubscriptionPlans = (snapshot) => {
    const plans = snapshot?.subscription_plans || [];
    if (!plans.length) return null;

    return (
      <Box>
        {renderSectionTitle(t('offers.subscriptionOptions', 'Subscription Options'), plans.length)}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {plans.map(plan => (
            <SubscriptionPlanDisplay
              key={plan.id}
              plan={plan}
              isAvailable={true}
              showSavings={true}
              variant="minimal"
              hasMultipleOptions={plans.length > 1}
              region={snapshot?.region}
              customDiscount360={snapshot?.custom_discount_360 ?? 20}
              customDiscount180={snapshot?.custom_discount_180 ?? 0}
            />
          ))}
        </Box>
      </Box>
    );
  };

  const renderComparisonMatrix = (item) => {
    const offers = item.offers || [];
    const firstSnapshot = getSnapshotFromOffer(offers[0]);
    const comparisonRegion = firstSnapshot?.region;
    const rates = firstSnapshot?.currencyRates || currencyRates;
    const fmtComparisonPrice = (input) => formatSnapshotPrice(input, {
      region: comparisonRegion,
      displayCurrency,
      rates,
    });

    return (
      <Box>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}>
          <Box>
            <Typography sx={{ fontWeight: 700, color: 'text.primary' }}>
              {t('history.offersInComparison', 'Offers in this comparison')}
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', mt: 0.25 }}>
              {t('comparison.detailedComparison', 'Detailed Comparison')}
            </Typography>
          </Box>
          <Chip
            label={`${offers.length} ${t('history.offers', 'offers')}`}
            size="small"
            sx={{ borderRadius: 1, bgcolor: '#fff7ed', color: '#92400e', fontWeight: 700 }}
          />
        </Box>

        <OfferComparisonTable
          offers={offers}
          t={t}
          fmtPrice={fmtComparisonPrice}
        />
      </Box>
    );
  };

  const renderSingleOfferDossier = (offer) => {
    const snapshot = getSnapshotFromOffer(offer);
    const activeProductTypes = getActiveProductTypes([snapshot].filter(Boolean));
    const oneTimeBlock = renderOneTimePayments(snapshot);
    const subscriptionBlock = renderSubscriptionPlans(snapshot);
    const monthlyTotal = getMonthlyTotal(offer);
    const offerName = offer?.tab_data?.name || t('offers.pricingOffer', 'Pricing Offer');
    const hasSideData = Boolean(oneTimeBlock || subscriptionBlock);

    return (
      <Card
        elevation={0}
        sx={{
          border: '1px solid #e5e7eb',
          borderRadius: 1,
          bgcolor: 'white',
          overflow: 'hidden',
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          p: 2,
          borderBottom: '1px solid #eef0f2',
          bgcolor: '#fff',
        }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: 'text.primary', mb: 0.75 }}>
              {offerName}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {snapshot?.region && (
                <Chip
                  label={snapshot.region.toUpperCase()}
                  size="small"
                  sx={{ height: 22, borderRadius: 1, bgcolor: '#eef6ff', color: '#00658d', fontWeight: 700 }}
                />
              )}
              <Chip
                label={`${activeProductTypes.length} ${t('comparison.products', 'products')}`}
                size="small"
                sx={{ height: 22, borderRadius: 1, bgcolor: '#f3f4f6', color: 'text.secondary', fontWeight: 600 }}
              />
              {(snapshot?.subscription_plans || []).length > 0 && (
                <Chip
                  label={`${snapshot.subscription_plans.length} ${t('offers.subscriptionOptions', 'Subscription Options').toLowerCase()}`}
                  size="small"
                  sx={{ height: 22, borderRadius: 1, bgcolor: '#ecfdf3', color: '#008a3d', fontWeight: 600 }}
                />
              )}
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography sx={{ color: '#00C853', fontWeight: 900, fontSize: '1.5rem', lineHeight: 1.1 }}>
              {formatDisplayPrice(monthlyTotal, displayCurrency, currencyRates, snapshot?.region)}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              {t('offers.perMonth', '/mo')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: hasSideData
            ? { xs: '1fr', lg: 'minmax(0, 1.6fr) minmax(340px, 0.85fr)' }
            : '1fr',
          gap: { xs: 2, lg: 3 },
          p: { xs: 2, md: 2.5 },
        }}>
          <Box>
            {renderSectionTitle(t('offers.productsAndUsage', 'Products and Usage'), activeProductTypes.length)}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {activeProductTypes.map(productType => (
                <ProductDisplay
                  key={productType}
                  snapshot={snapshot}
                  productType={productType}
                  variant="compact"
                  showNotIncluded={false}
                  context="history"
                />
              ))}
            </Box>
          </Box>

          {hasSideData && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              borderLeft: { xs: 'none', lg: '1px solid #eef0f2' },
              pl: { xs: 0, lg: 3 },
            }}>
              {oneTimeBlock}
              {subscriptionBlock}
            </Box>
          )}
        </Box>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh' 
      }}>
        <CircularProgress sx={{ color: '#00C853' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
        <AppBar position="static" elevation={0} sx={{ 
          bgcolor: '#fff', 
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
        }}>
          <Toolbar>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/')}
              sx={{ 
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' }
              }}
            >
              {t('common.backToCalculator', 'Back to Calculator')}
            </Button>
          </Toolbar>
        </AppBar>
        
        <Box sx={{ p: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      {/* Animation styles */}
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ 
        bgcolor: '#fff', 
        color: 'text.primary',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
      }}>
        <Toolbar>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(isAdminView ? '/admin/users' : '/')}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' }
            }}
          >
            {isAdminView
              ? t('admin.backToUsers', 'Back to Users')
              : t('common.backToCalculator', 'Back to Calculator')}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <History sx={{ color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">
              {isAdminView
                ? t('admin.userHistoryShort', 'User History')
                : t('history.title', 'Offers History')}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{
        p: { xs: 2, md: 4 },
        maxWidth: '100vw',
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            {isAdminView && viewingUser
              ? t('admin.userHistory', '{{name}} — History', { name: viewingUser.nickname || viewingUser.email })
              : t('history.title', 'Offers History')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {isAdminView && viewingUser
              ? viewingUser.email
              : t('history.description', 'View and manage all your generated offers and comparisons')}
          </Typography>
        </Box>

        {/* Modern Filters Panel */}
        <Paper elevation={0} sx={{ 
          borderRadius: 3,
          border: 'none',
          p: 2,
          mb: 2.5,
          bgcolor: 'white',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}>
          <HistoryFiltersPanel
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateChange={handleDateRangeChange}
            onClear={handleClearFilters}
            placeholder={t('history.selectDateRange', 'Select date range')}
            searchValue={clientName}
            onSearchChange={setClientName}
            searchPlaceholder={t('history.searchByClientName', 'Search by company name...')}
          />
        </Paper>
        
        {/* Results Info */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2,
          px: 1
        }}>
          <Typography variant="body2" color="text.secondary">
            {t('history.showingResults', 'Showing {{showing}} of {{total}} items', { 
              showing: historyItems.length,
              total: totalItems 
            })}
          </Typography>
        </Box>

        {/* Offers Table */}
        <Paper elevation={0} sx={{
          borderRadius: 3,
          border: 'none',
          overflow: 'hidden',
          bgcolor: 'white',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          minHeight: 400,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <TableContainer sx={{ flex: 1, overflowX: 'auto', overflowY: 'visible', bgcolor: 'white' }}>
            <Table sx={{ bgcolor: 'white', minWidth: { xs: 860, md: 1040 } }}>
              <TableHead>
                <TableRow sx={{
                  bgcolor: '#f8f9fa',
                  '& > .MuiTableCell-root': {
                    bgcolor: '#f8f9fa',
                  },
                }}>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {t('history.dateTime', 'Date & Time')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {t('history.type', 'Type')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {t('history.clientName', 'Company Name')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {t('history.userEmail', 'User Email')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {t('history.link', 'Link')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary', width: 50 }}>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <AnimatePresence mode="wait">
                  {tableLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 8 }}>
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <CircularProgress sx={{ color: '#00C853' }} />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            {t('history.loadingResults', 'Loading results...')}
                          </Typography>
                        </motion.div>
                      </TableCell>
                    </TableRow>
                  ) : historyItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 8 }}>
                      <History sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                        {totalItems === 0 
                          ? t('history.noHistory', 'No offers or comparisons yet')
                          : t('history.noFilteredResults', 'No items found for selected date range')
                        }
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {totalItems === 0
                          ? t('history.createFirst', 'Create your first offer or comparison in the calculator')
                          : t('history.adjustFilters', 'Try adjusting your date filters to see more results')
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                  ) : (
                    historyItems.map((item, index) => (
                      <React.Fragment key={item.id}>
                        {/* Main row (offer or comparison) */}
                        <TableRow 
                          sx={{ 
                            '& > .MuiTableCell-root': {
                              bgcolor: 'white',
                            },
                            '&:hover > .MuiTableCell-root': {
                              bgcolor: 'rgba(0, 200, 83, 0.02)',
                            },
                            animation: `fadeInUp 0.4s ease-out ${index * 0.05}s both`
                          }}
                        >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatDateTime(item.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2, px: 2, width: 56 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {item.type === 'comparison' ? (
                              <>
                                <Chip
                                  label={t('history.comparison', 'Comparison')}
                                  size="small"
                                  sx={{
                                    bgcolor: '#fed7aa',
                                    color: '#92400e',
                                    fontWeight: 500,
                                    fontSize: 12,
                                    height: 24,
                                    borderRadius: 3
                                  }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  ({item.totalOffers} {t('history.offers', 'offers')})
                                </Typography>
                              </>
                            ) : (
                              <>
                                <Chip
                                  label={t('history.offer', 'Offer')}
                                  size="small"
                                  sx={{
                                    bgcolor: '#d1fae5',
                                    color: '#065f46',
                                    fontWeight: 500,
                                    fontSize: 12,
                                    height: 24,
                                    borderRadius: 3
                                  }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  ({(() => {
                                    const snapshot = getSnapshotFromOffer(item.offer);
                                    return formatDisplayPrice(item.totalMonthly, displayCurrency, currencyRates, snapshot?.region);
                                  })()}/mo)
                                </Typography>
                              </>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.primary" sx={{ fontWeight: item.clientName ? 500 : 400 }}>
                            {item.clientName || t('history.noClientName', 'No company name')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {item.userEmail}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2, px: 2 }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {(() => {
                              const canRestore = item.type === 'comparison'
                                ? buildComparisonTemplatePayloads({ id: item.id, offers: item.offers }).length > 0
                                : canUseOfferAsTemplate(item.offer);

                              return (
                                <Tooltip title={canRestore ? t('history.useAsTemplate', 'Use as template') : t('history.templateUnavailable', 'Template data unavailable')}>
                                  <span>
                                    <IconButton
                                      onClick={() => handleUseAsTemplate(item)}
                                      disabled={!canRestore || templateLoadingId === item.id}
                                      aria-label={canRestore ? t('history.useAsTemplate', 'Use as template') : t('history.templateUnavailable', 'Template data unavailable')}
                                      size="small"
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: 1.5,
                                        border: '1px solid #e5e7eb',
                                        bgcolor: 'white',
                                        color: '#6b7280',
                                        '&:hover': {
                                          bgcolor: '#f3f4f6',
                                          borderColor: '#d1d5db',
                                          color: '#00C853'
                                        },
                                        '&.Mui-disabled': {
                                          bgcolor: '#f9fafb',
                                          color: '#d1d5db'
                                        }
                                      }}
                                    >
                                      {templateLoadingId === item.id ? (
                                        <CircularProgress size={16} sx={{ color: '#00C853' }} />
                                      ) : (
                                        <RestorePage sx={{ fontSize: 16 }} />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              );
                            })()}
                            <Tooltip title={t('history.openInNewTab', 'Open in new tab')}>
                              <IconButton
                                onClick={() => handleOpenLink(item.link)}
                                size="small"
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 1.5,
                                  border: '1px solid #e5e7eb',
                                  bgcolor: 'white',
                                  color: '#6b7280',
                                  '&:hover': {
                                    bgcolor: '#f3f4f6',
                                    borderColor: '#d1d5db',
                                    color: '#00C853'
                                  }
                                }}
                              >
                                <OpenInNew sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={copiedItemId === item.id ? t('history.copied', 'Copied!') : t('history.copyLink', 'Copy link')}>
                              <IconButton
                                onClick={() => handleCopyLink(item.link, item.id)}
                                size="small"
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 1.5,
                                  border: '1px solid #e5e7eb',
                                  bgcolor: copiedItemId === item.id ? '#d1fae5' : 'white',
                                  color: copiedItemId === item.id ? '#00C853' : '#6b7280',
                                  '&:hover': {
                                    bgcolor: copiedItemId === item.id ? '#d1fae5' : '#f3f4f6',
                                    borderColor: copiedItemId === item.id ? '#00C853' : '#d1d5db',
                                    color: '#00C853'
                                  }
                                }}
                              >
                                <ContentCopy sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 2, px: 2 }}>
                          {/* Показуємо кнопку розгортання тільки для порівнянь або оферів з деталями */}
                          {(item.type === 'comparison' && item.offers?.length > 0) || (item.type === 'offer' && item.offer) ? (
                            <IconButton 
                              size="small" 
                              onClick={() => toggleExpanded(item.id)}
                              sx={{ 
                                width: 32,
                                height: 32,
                                borderRadius: 1.5,
                                border: '1px solid #e5e7eb',
                                bgcolor: 'white',
                                color: '#6b7280',
                                '&:hover': {
                                  bgcolor: '#f3f4f6',
                                  borderColor: '#d1d5db'
                                }
                              }}
                            >
                              {expandedComparison === item.id ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          ) : null}
                        </TableCell>
                      </TableRow>

                      {/* Expanded details row */}
                      <TableRow>
                        <TableCell colSpan={6} sx={{ p: 0, border: 'none' }}>
                          <Collapse in={expandedComparison === item.id}>
                            <Box sx={{
                              p: { xs: 2, md: 3 },
                              bgcolor: '#f8fafc',
                              borderTop: '1px solid #e5e7eb',
                            }}>
                              {item.type === 'comparison' ? (
                                renderComparisonMatrix(item)
                              ) : (
                                renderSingleOfferDossier(item.offer)
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                  )}
                </AnimatePresence>
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              p: 3,
              borderTop: '1px solid rgba(0, 0, 0, 0.06)'
            }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: '#6b7280',
                    fontWeight: 500,
                    borderRadius: 2,
                    '&.Mui-selected': {
                      backgroundColor: '#00C853',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#00A843'
                      }
                    },
                    '&:hover': {
                      backgroundColor: '#f3f4f6'
                    }
                  }
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default OffersHistoryPage;
